import React from 'react';
import { Button } from '../ui/button';

/**
 * Error Boundary component to catch React errors and prevent full app crashes.
 * Displays a fallback UI with retry/reload options.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Store error info for display
    this.setState({ errorInfo });
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h1>
            
            <p className="text-muted-foreground mb-6">
              We're sorry, but something unexpected happened. Our team has been notified.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-muted rounded-lg text-left overflow-auto max-h-40">
                <p className="text-sm font-mono text-red-600">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </Button>
              <Button
                variant="secondary"
                onClick={this.handleReload}
                className="px-4 py-2 rounded-lg"
              >
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper for specific feature sections.
 * Renders an inline error card with a retry button instead of crashing the page.
 * Uses a key-based reset: incrementing the key remounts the inner ErrorBoundary,
 * giving the children a fresh chance to render.
 */
class FeatureErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { retryCount: 0 };
  }

  handleRetry = () => {
    this.setState(prev => ({ retryCount: prev.retryCount + 1 }));
  };

  render() {
    const { children, featureName } = this.props;
    return (
      <ErrorBoundary
        key={this.state.retryCount}
        fallback={
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-800 dark:text-red-300 font-medium">
              Unable to load {featureName || 'this section'}
            </p>
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">
              Something went wrong loading this section.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={this.handleRetry}
              className="mt-3 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50"
            >
              Try Again
            </Button>
          </div>
        }
      >
        {children}
      </ErrorBoundary>
    );
  }
}

export const FeatureErrorBoundary = ({ children, featureName }) => (
  <FeatureErrorBoundaryInner featureName={featureName}>
    {children}
  </FeatureErrorBoundaryInner>
);

export default ErrorBoundary;
